import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Content,
  Flex,
  FlexItem,
  Icon,
  Modal,
  ModalBody,
  ModalHeader,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Spinner,
  Title,
  Wizard,
  WizardStep,
} from '@patternfly/react-core';
import { DomainIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { useNotifications } from '../../../../contexts/NotificationContext';
import { useUserContext } from '../../../../contexts/user/UserContext';
import type { PaymentSource } from '../../../../types/models/Payment';
import { useDomainNames } from '../../../../hooks/useDomainNames';
import { useDomainContact } from '../../../../hooks/useDomainContact';
import { useCreateDomainOrder } from '../../../../hooks/useCreateDomainOrder';
import { useDomainOrderPreview } from '../../../../hooks/useDomainOrderPreview';
import type { DomainTradeOrderCreateRequest } from '../../../../types/models';
import ContactStep from '../Register/ContactStep';
import { RecapFooter } from '../Register/RecapStep';
import { EMPTY_CONTACT, buildContactRequest, isContactDraftValid } from '../Register/types';
import type { ContactDraft, ContactMode } from '../Register/types';
import TradeDomainsStep from './TradeDomainsStep';
import TradeRecapStep from './TradeRecapStep';
import { MAX_TRADE_CART, TRADE_STEP_IDS, tradeIneligibilityReason } from './types';

/**
 * The INTERNAL TRADE wizard — change the REGISTRANT ("owner") of `.be` domains the tenant already
 * owns here (for `.be` a separate legal operation at DNS Belgium, performed by the backend worker
 * via the registrar; invoiced like a normal transfer). A sibling of the Register/Transfer wizards:
 * pick eligible domains → the NEW registrant contact → recap (exact `preview?type=trade` pricing) →
 * POST `{type: 'trade'}` → Mollie redirect (or the staff waived-payment short-circuit).
 */
const DomainTrade: React.FunctionComponent = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addNotification, setDrawerOpen } = useNotifications();

  // --- Read hooks (kept at the page level so their state survives step navigation) ---
  const { domainNames, loading: domainNamesLoading, error: domainNamesError, refetch } = useDomainNames();
  const { create, loading: creating } = useCreateDomainOrder();
  const { preview, loading: previewLoading, error: previewError, fetchPreview } = useDomainOrderPreview();

  // --- Wizard state (lifted above <Wizard>) ---
  // Step 1 — the picked FQDNs. Per-name create-time rejection codes (keyed by FQDN) are surfaced
  // on the matching rows after a failed submit — any rejected name fails the WHOLE order.
  const [selected, setSelected] = useState<string[]>([]);
  const [itemErrors, setItemErrors] = useState<Record<string, string>>({});

  // Step 2 — the NEW registrant contact (same helpers as the register/transfer wizards).
  const [contactMode, setContactMode] = useState<ContactMode>('new');
  const [contact, setContact] = useState<ContactDraft>(EMPTY_CONTACT);
  const [contactErrors, setContactErrors] = useState<Record<string, string>>({});
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<number>(0);
  const { contact: portfolioContact, loading: portfolioContactLoading } = useDomainContact(selectedPortfolioId);

  // Step 3 — recap
  const [agreementsAccepted, setAgreementsAccepted] = useState(false);
  // Payment source — prepaid balance / saved card / hosted checkout (default). Ignored for a real
  // staff creator (the backend waives the payment), so the payload omits `payment` for staff.
  const [paymentSource, setPaymentSource] = useState<PaymentSource>('checkout');
  const [saveCard, setSaveCard] = useState(false);
  const [paying, setPaying] = useState(false);
  const user = useUserContext();
  const isStaff = user?.is_ancalagon_staff === true;

  // Entry-point deep link: `/datacenter/domains/trade?domain=<fqdn>` (the row/detail "Change owner"
  // action) pre-selects that domain — applied EXACTLY ONCE, after the list has settled, and only if
  // the domain is actually eligible (the guard re-runs on refetches, so the ref keeps a later
  // background reload from clobbering the user's edited selection).
  const preselectApplied = useRef(false);
  useEffect(() => {
    if (preselectApplied.current || domainNamesLoading) {
      return;
    }
    preselectApplied.current = true;
    const requested = searchParams.get('domain')?.trim().toLowerCase();
    if (!requested) {
      return;
    }
    const match = domainNames.find((d) => d.name === requested);
    if (match && tradeIneligibilityReason(match) === null) {
      setSelected([match.name]);
    }
  }, [domainNames, domainNamesLoading, searchParams]);

  // Apply a portfolio selection to the contact form EXACTLY ONCE per selection (identical to the
  // registration/transfer wizards — keying on the applied id, not the reference, so a background
  // OIDC silent renew's refetch can't clobber later edits; and only once the fetch for THIS id has
  // settled).
  const appliedPortfolioId = useRef<number>(0);
  useEffect(() => {
    if (selectedPortfolioId <= 0 || portfolioContactLoading) {
      return;
    }
    const settledForThisId = portfolioContact === null || portfolioContact.domain_name_id === selectedPortfolioId;
    if (!settledForThisId || appliedPortfolioId.current === selectedPortfolioId) {
      return;
    }
    appliedPortfolioId.current = selectedPortfolioId;
    if (portfolioContact) {
      setContact({
        first_name: portfolioContact.first_name ?? '',
        last_name: portfolioContact.last_name ?? '',
        organization: portfolioContact.organization ?? '',
        email: portfolioContact.email ?? '',
        phone: portfolioContact.phone ?? '',
        address_line1: portfolioContact.address_line1 ?? '',
        address_line2: portfolioContact.address_line2 ?? '',
        city: portfolioContact.city ?? '',
        state: portfolioContact.state ?? '',
        zipcode: portfolioContact.zipcode ?? '',
        countrycode: portfolioContact.countrycode ?? '',
      });
    } else {
      setContact(EMPTY_CONTACT);
    }
    setContactErrors({});
  }, [selectedPortfolioId, portfolioContact, portfolioContactLoading]);

  const handleToggle = useCallback((name: string, isSelecting: boolean) => {
    setSelected((prev) => {
      if (isSelecting) {
        if (prev.includes(name) || prev.length >= MAX_TRADE_CART) {
          return prev;
        }
        return [...prev, name];
      }
      return prev.filter((n) => n !== name);
    });
    // Editing a row invalidates its stale create-time rejection.
    setItemErrors((prev) => {
      if (!(name in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const handleToggleAll = useCallback((names: string[], isSelecting: boolean) => {
    setSelected((prev) => {
      if (!isSelecting) {
        return prev.filter((n) => !names.includes(n));
      }
      const merged = [...prev];
      for (const name of names) {
        if (!merged.includes(name) && merged.length < MAX_TRADE_CART) {
          merged.push(name);
        }
      }
      return merged;
    });
  }, []);

  const onContactFieldChange = useCallback((field: keyof ContactDraft, value: string) => {
    setContact((prev) => ({ ...prev, [field]: value }));
    setContactErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const portfolioContactMissing = selectedPortfolioId > 0 && !portfolioContactLoading && portfolioContact === null;

  // --- Per-step validity (gates the footer's Next / Confirm) ---
  const domainsValid = selected.length > 0;
  const contactValid = isContactDraftValid(contact);
  const hasUnsupported = preview ? preview.items.some((i) => !i.supported) : false;
  const canSubmit = domainsValid && contactValid && agreementsAccepted && !hasUnsupported && !creating;

  const handleConfirm = useCallback(async (): Promise<{ jumpToStepId?: string }> => {
    // Clear per-row errors from any previous attempt so a resubmit starts clean.
    setItemErrors({});

    // A trade order takes NO dns_endpoint_id/nameservers (nothing about the delegation changes) and
    // NO keep_contact (contradictory — the whole point is the new registrant); the backend rejects
    // them loud, so this payload never carries those keys.
    const payload: DomainTradeOrderCreateRequest = {
      type: 'trade',
      domains: selected,
      contact: buildContactRequest(contact),
      agreements_accepted: true,
      ...(isStaff
        ? {}
        : {
            payment: {
              source: paymentSource,
              ...(paymentSource === 'checkout' && saveCard ? { save_card: true } : {}),
            },
          }),
    };

    const outcome = await create(payload);

    if (outcome.status === 'success') {
      if (outcome.data.payment_waived) {
        // Staff order — the backend waived the Mollie payment (there is no checkout to redirect
        // to): the order is already `paid` and awaits the fulfilment worker; an unpaid invoice
        // will be issued to the client. Land on the shared order status page.
        addNotification({
          title: 'Owner-change order created — payment waived (staff)',
          srTitle: 'Success alert',
          description:
            'No online payment is needed for a staff order. The owner change will be applied automatically and an unpaid invoice will be issued to the client.',
          variant: 'success',
        });
        setDrawerOpen(true);
        navigate(`/domain-orders/${outcome.data.order_id}/return`);
        return {};
      }
      if (outcome.data.checkout_url === null) {
        // Paid without a redirect: the prepaid balance was debited inside the order transaction
        // (already `paid`), or the saved card was charged in the background (`paid` once Mollie
        // settled it, else the return page's poll sees it flip). Land on the shared status page.
        if (outcome.data.payment_source === 'wallet') {
          addNotification({
            title: 'Order paid from your prepaid balance',
            srTitle: 'Success alert',
            description: 'Your balance was debited and the owner change starts automatically.',
            variant: 'success',
          });
        } else if (outcome.data.paid) {
          addNotification({
            title: 'Order paid with your saved card',
            srTitle: 'Success alert',
            description: 'Your saved card was charged and the owner change starts automatically.',
            variant: 'success',
          });
        } else {
          addNotification({
            title: 'Charging your saved card',
            srTitle: 'Info alert',
            description: "Your saved card is being charged — we'll confirm the payment in a moment.",
            variant: 'info',
          });
        }
        setDrawerOpen(true);
        navigate(`/domain-orders/${outcome.data.order_id}/return`);
        return {};
      }
      // Mirror the register/transfer Pay flow: show the non-dismissible please-wait modal, then hand
      // off to Mollie. Deliberately leave `paying` true — the page is unloading.
      setPaying(true);
      window.location.href = outcome.data.checkout_url;
      return {};
    }

    const fe = outcome.fieldErrors;
    if (fe.contact && typeof fe.contact === 'object') {
      setContactErrors(fe.contact);
    }
    // The trade per-name rejection is either a `{name: code}` map (flag those step-1 rows) or a
    // flat string (no single row to attach it to — surface it in the notification instead).
    if (fe.domains && typeof fe.domains === 'object') {
      setItemErrors(fe.domains);
    }
    const domainsMessage = typeof fe.domains === 'string' ? fe.domains : null;
    addNotification({
      title: 'Could not start the owner change',
      srTitle: 'Error alert',
      description: domainsMessage ?? fe.payment ?? outcome.message,
      variant: 'danger',
    });
    setDrawerOpen(true);

    let jumpToStepId: string | undefined;
    if (fe.domains) {
      jumpToStepId = TRADE_STEP_IDS.domains;
    } else if (fe.contact) {
      jumpToStepId = TRADE_STEP_IDS.contact;
    } else if (fe.payment) {
      jumpToStepId = TRADE_STEP_IDS.recap;
    }
    return { jumpToStepId };
  }, [selected, contact, create, addNotification, setDrawerOpen, navigate, isStaff, paymentSource, saveCard]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="DomainTradeBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem to="/datacenter/domains">Domain Names</BreadcrumbItem>
          <BreadcrumbItem isActive>Change owner</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <DomainIcon />
          </Icon>
          Change a domain&apos;s owner
        </Content>
        <Content component="p">
          Change the registrant (legal owner) of a <strong>.be</strong> domain you already manage with us: pick the
          domains, enter the new owner contact, then pay to start the change.
        </Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <Wizard navAriaLabel="Domain owner-change steps" onClose={() => navigate('/datacenter/domains')}>
          <WizardStep name="Domains" id={TRADE_STEP_IDS.domains} footer={{ isNextDisabled: !domainsValid }}>
            <TradeDomainsStep
              domainNames={domainNames}
              loading={domainNamesLoading}
              error={domainNamesError}
              onRetry={() => void refetch()}
              selected={selected}
              onToggle={handleToggle}
              onToggleAll={handleToggleAll}
              itemErrors={itemErrors}
            />
          </WizardStep>

          <WizardStep name="New owner" id={TRADE_STEP_IDS.contact} footer={{ isNextDisabled: !contactValid }}>
            <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsMd' }}>
              <FlexItem>
                <Title headingLevel="h2" size="lg">
                  New registrant contact
                </Title>
                <Content component="p">
                  Enter the <strong>new</strong> owner (registrant) of the selected domains. This contact replaces the
                  current registrant at the registry for every domain in this order.
                </Content>
              </FlexItem>
              <FlexItem>
                <ContactStep
                  hideIntro
                  mode={contactMode}
                  onModeChange={setContactMode}
                  contact={contact}
                  onFieldChange={onContactFieldChange}
                  errors={contactErrors}
                  domainNames={domainNames}
                  domainNamesLoading={domainNamesLoading}
                  selectedPortfolioId={selectedPortfolioId}
                  onSelectPortfolio={setSelectedPortfolioId}
                  portfolioContactLoading={portfolioContactLoading}
                  portfolioContactMissing={portfolioContactMissing}
                />
              </FlexItem>
            </Flex>
          </WizardStep>

          <WizardStep
            name="Review & pay"
            id={TRADE_STEP_IDS.recap}
            footer={<RecapFooter canSubmit={canSubmit} submitting={creating || paying} onConfirm={handleConfirm} />}
          >
            <TradeRecapStep
              domains={selected}
              contact={contact}
              preview={preview}
              previewLoading={previewLoading}
              previewError={previewError}
              onRequestPreview={fetchPreview}
              agreementsAccepted={agreementsAccepted}
              onAgreementsChange={setAgreementsAccepted}
              paymentSource={paymentSource}
              onPaymentSourceChange={setPaymentSource}
              saveCard={saveCard}
              onSaveCardChange={setSaveCard}
            />
          </WizardStep>
        </Wizard>
      </PageSection>

      {/* Mirrors the register/transfer Pay flow: a non-dismissible please-wait modal bridging the
          click to the Mollie redirect. disableFocusTrap is load-bearing — this modal has no focusable
          element, and the focus-trap throws at render with nothing to focus. */}
      <Modal variant="small" isOpen={paying} aria-label="Generating payment" disableFocusTrap>
        <ModalHeader title="Generating your payment" />
        <ModalBody>
          <Flex
            direction={{ default: 'column' }}
            alignItems={{ default: 'alignItemsCenter' }}
            spaceItems={{ default: 'spaceItemsMd' }}
          >
            <FlexItem>
              <Spinner size="xl" aria-label="Generating payment" />
            </FlexItem>
            <FlexItem>
              <Content component="p" className="pf-v6-u-text-align-center">
                Preparing your secure payment. You will be redirected to our payment provider in a moment — please keep
                this window open.
              </Content>
            </FlexItem>
          </Flex>
        </ModalBody>
      </Modal>
    </>
  );
};

export default DomainTrade;
